import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBookOpen,
  FaChalkboardTeacher,
  FaEnvelope,
  FaHome,
  FaKey,
  FaLifeRing,
  FaLock,
  FaRocket,
  FaSchool,
  FaSignOutAlt,
  FaStar,
} from "react-icons/fa";
import PasswordResetModal from "./PasswordResetModal";
import TeacherAboutModal from "./TeacherAboutModal";
import { useAppTheme } from "../context/AppThemeContext";

const PREMIUM_PLAN_SET = new Set(["PREMIUM", "GROWTH", "ENTERPRISE", "PRO"]);
const buildAboutStorageKey = (email) => `teacherProfileAbout:${email || "anonymous"}`;

export default function TeacherProfilePage({
  displayName,
  initials,
  email,
  roleLabel,
  dashboardPath,
  teacherName,
  teacherSchoolName,
  teacherSubject,
  teacherProfileLoading,
  subscriptionInfo,
  showResetForm,
  setShowResetForm,
  newPassword,
  setNewPassword,
  isSubmitting,
  handleResetPassword,
  handleCopyEmail,
  handleLogout,
}) {
  const { isDarkMode } = useAppTheme();
  const isPremium = PREMIUM_PLAN_SET.has(String(subscriptionInfo?.plan || "").trim().toUpperCase());

  const [aboutText, setAboutText] = useState("");
  const [draftAboutText, setDraftAboutText] = useState("");
  const [isEditingAbout, setIsEditingAbout] = useState(false);

  useEffect(() => {
    const key = buildAboutStorageKey(email);
    try { const s = localStorage.getItem(key) || ""; setAboutText(s); setDraftAboutText(s); } catch { setAboutText(""); setDraftAboutText(""); }
  }, [email]);

  const handleStartAboutEdit = () => { setDraftAboutText(aboutText); setIsEditingAbout(true); };
  const handleCancelAboutEdit = () => { setDraftAboutText(aboutText); setIsEditingAbout(false); };
  const handleSaveAbout = () => {
    const next = draftAboutText.trim();
    try { localStorage.setItem(buildAboutStorageKey(email), next); } catch {}
    setAboutText(next); setDraftAboutText(next); setIsEditingAbout(false);
  };

  const bg = isDarkMode ? "bg-slate-950" : "bg-slate-50";
  const card = isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200";
  const cardHeader = isDarkMode ? "bg-slate-800/60 border-slate-700" : "bg-slate-50 border-slate-200";
  const text = isDarkMode ? "text-white" : "text-slate-900";
  const muted = isDarkMode ? "text-slate-400" : "text-slate-500";
  const subtle = isDarkMode ? "text-slate-300" : "text-slate-600";
  const statBg = isDarkMode ? "bg-slate-800/60 border-slate-700/50" : "bg-slate-50 border-slate-200";
  const divider = isDarkMode ? "border-slate-800" : "border-slate-200";
  const actionHover = isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-50";
  const iconBox = isDarkMode ? "bg-slate-800 text-teal-400" : "bg-teal-50 text-teal-600";

  const stats = [
    { label: "School", value: teacherSchoolName, icon: <FaSchool /> },
    { label: "Subject", value: teacherSubject, icon: <FaBookOpen /> },
    { label: "Role", value: roleLabel, icon: <FaChalkboardTeacher /> },
    { label: "Plan", value: subscriptionInfo?.plan || "N/A", icon: isPremium ? <FaStar /> : <FaRocket /> },
    { label: "Plan Start", value: subscriptionInfo?.startDate || "N/A", icon: <FaStar /> },
    { label: "Status", value: teacherProfileLoading ? "Refreshing" : "Active", icon: <FaStar /> },
  ];

  return (
    <div className={`min-h-full px-4 py-8 md:px-6 md:py-10 ${bg}`}>
      <div className="mx-auto max-w-5xl space-y-5">

        {/* Hero */}
        <div className={`overflow-hidden rounded-2xl border ${card} shadow-sm`}>
          <div className="h-20 bg-gradient-to-r from-teal-700 via-teal-600 to-blue-600" />
          <div className="px-5 pb-5 sm:px-6 sm:pb-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex items-end gap-4 -mt-10">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-600 to-blue-600 text-2xl font-black text-white shadow-lg ring-4 ring-slate-900/80 sm:h-24 sm:w-24 sm:text-3xl">
                  {initials || "TP"}
                </div>
                <div className="pb-1">
                  <h1 className={`text-xl font-bold sm:text-2xl ${text}`}>{teacherName || displayName}</h1>
                  <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-teal-600/20 px-2.5 py-0.5 text-xs font-semibold text-teal-400">
                    <FaChalkboardTeacher className="text-[10px]" /> {roleLabel}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pb-1 sm:justify-end">
                <button type="button" onClick={handleStartAboutEdit}
                  className="inline-flex items-center gap-2 rounded-xl border border-teal-600/40 bg-teal-600/10 px-4 py-2 text-sm font-semibold text-teal-400 transition hover:bg-teal-600/20">
                  ✏ Edit About
                </button>
                <Link to={dashboardPath}
                  className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
                  <FaHome className="text-xs" /> Dashboard
                </Link>
              </div>
            </div>
            <div className={`mt-4 flex flex-wrap items-center gap-3 text-sm ${subtle}`}>
              <span className="flex items-center gap-1.5"><FaEnvelope className={`text-xs ${muted}`} />{email || "No email"}</span>
              {teacherSchoolName && (<><span className={muted}>·</span><span className="flex items-center gap-1.5"><FaSchool className={`text-xs ${muted}`} />{teacherSchoolName}</span></>)}
              <span className={`ml-auto text-xs ${teacherProfileLoading ? "text-amber-400" : "text-teal-400"}`}>
                ● {teacherProfileLoading ? "Refreshing" : "Active"}
              </span>
            </div>
            {aboutText && (
              <div className={`mt-4 rounded-xl border p-4 text-sm leading-6 ${statBg} ${subtle}`}>
                <p className={`mb-1 text-[10px] font-semibold uppercase tracking-wider ${muted}`}>About</p>
                {aboutText}
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className={`rounded-2xl border ${card} p-5 shadow-sm sm:p-6`}>
          <p className={`mb-4 text-xs font-semibold uppercase tracking-widest ${muted}`}>Teacher Details</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {stats.map((s) => (
              <div key={s.label} className={`flex flex-col gap-2 rounded-xl border p-3 ${statBg}`}>
                <div className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs ${iconBox}`}>{s.icon}</div>
                <p className={`text-[10px] font-semibold uppercase tracking-wider ${muted}`}>{s.label}</p>
                <p className={`text-sm font-semibold leading-tight ${text}`}>{s.value || "—"}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Subscription */}
        {subscriptionInfo && (
          <div className={`rounded-2xl border ${card} p-5 shadow-sm sm:p-6`}>
            <p className={`mb-4 text-xs font-semibold uppercase tracking-widest ${muted}`}>Subscription</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { label: "Plan", value: subscriptionInfo.plan },
                { label: "Start", value: subscriptionInfo.startDate },
                { label: "Expiry", value: subscriptionInfo.expiryDate },
                { label: "Duration", value: subscriptionInfo.duration },
              ].map((item) => (
                <div key={item.label} className={`rounded-xl border p-3 ${statBg}`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider ${muted}`}>{item.label}</p>
                  <p className={`mt-1 text-sm font-semibold ${text}`}>{item.value || "N/A"}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security + Actions */}
        <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
          <div className={`overflow-hidden rounded-2xl border ${card} shadow-sm`}>
            <div className={`flex items-center justify-between border-b px-5 py-4 ${cardHeader} ${divider}`}>
              <div>
                <p className={`text-xs font-semibold uppercase tracking-widest ${muted}`}>Security</p>
                <h2 className={`mt-0.5 text-base font-bold ${text}`}>Password & sign-in</h2>
              </div>
              <button type="button" onClick={() => setShowResetForm((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
                <FaLock className="text-[10px]" />{showResetForm ? "Close" : "Change Password"}
              </button>
            </div>
            <div className="p-5">
              <div className={`rounded-xl border p-4 text-sm ${statBg}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${muted}`}>Work Email</p>
                <p className={`font-semibold break-all ${text}`}>{email || "Not available"}</p>
              </div>
              <ul className={`mt-4 space-y-2 text-xs leading-5 ${muted}`}>
                <li className="flex gap-2"><span className="text-teal-500 mt-0.5">✓</span> Use a unique password for this account.</li>
                <li className="flex gap-2"><span className="text-teal-500 mt-0.5">✓</span> Sign in again after updating so the new credential takes effect.</li>
                <li className="flex gap-2"><span className="text-teal-500 mt-0.5">✓</span> Contact support if you get locked out.</li>
              </ul>
            </div>
          </div>

          <div className={`rounded-2xl border ${card} p-5 shadow-sm`}>
            <p className={`mb-4 text-xs font-semibold uppercase tracking-widest ${muted}`}>Quick Actions</p>
            <div className="space-y-2">
              {[
                { label: "Dashboard", desc: "Back to teaching tools", icon: <FaHome />, kind: "link", to: dashboardPath },
                { label: "Reset Password", desc: "Update your credentials", icon: <FaKey />, kind: "button", onClick: () => setShowResetForm(true) },
                { label: "Contact Support", desc: "Get help from ClassNX", icon: <FaLifeRing />, kind: "external", href: "mailto:support@classnx.com" },
              ].map((action) => {
                const inner = (<><div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm ${iconBox}`}>{action.icon}</div><div><p className={`text-sm font-semibold ${text}`}>{action.label}</p><p className={`text-xs ${muted}`}>{action.desc}</p></div></>);
                const cls = `flex items-center gap-3 rounded-xl border p-3 transition ${statBg} ${actionHover}`;
                if (action.kind === "link") return <Link key={action.label} to={action.to} className={cls}>{inner}</Link>;
                if (action.kind === "external") return <a key={action.label} href={action.href} className={cls}>{inner}</a>;
                return <button key={action.label} type="button" onClick={action.onClick} className={`w-full text-left ${cls}`}>{inner}</button>;
              })}
            </div>

          </div>
        </div>

      </div>

      <PasswordResetModal
        isOpen={showResetForm}
        title="Reset teacher password"
        description="Update the password for this teacher account."
        email={email}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        isSubmitting={isSubmitting}
        onClose={() => setShowResetForm(false)}
        onSubmit={handleResetPassword}
      />
      <TeacherAboutModal
        isOpen={isEditingAbout}
        value={draftAboutText}
        setValue={setDraftAboutText}
        onClose={handleCancelAboutEdit}
        onSave={handleSaveAbout}
      />
    </div>
  );
}
